import type { UseFormReturn } from 'react-hook-form';

import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ActivityId } from '~/api/user/types';

import { useUser } from '~/api/user';
import { Checkbox, Field, FieldGroup, Input, Radio } from '~/components/Form';
import { Activity, availableActivities, availablePrimaryActivities, getSecondaryActivityOptions } from '~/constants/constants';
import { filterBoolean, includes } from '~/utils/arrays';
import { isNotBlank } from '~/utils/string';

import type { ActivitiesFormType } from './types';

import { primaryActivitiesStyle, secondaryActivitiesStyle, wrapperStyle } from './Activities.css';
import { StylingAndInteriorWarning } from './components/StylingAndInteriorWarning';

const otherActivityIndex = availableActivities.findIndex((activity) => activity === Activity.other);

export const Activities = ({ form }: { form: UseFormReturn<ActivitiesFormType> }) => {
  const { data: user } = useUser();

  const { control, setValue } = form;
  const { t } = useTranslation(['common', 'settings', 'validation']);

  const primaryActivity = useWatch({ control, name: 'primaryActivity' });
  const secondaryActivities = useWatch({ control, name: 'secondaryActivities' });
  const isOtherActivityChecked = secondaryActivities[otherActivityIndex];

  useEffect(() => {
    if (!isOtherActivityChecked) setValue('otherActivityInformation', '');
  }, [isOtherActivityChecked, setValue]);

  useEffect(() => {
    const index = availableActivities.findIndex((activity) => activity === primaryActivity);

    if (index !== -1) setValue(`secondaryActivities.${index}`, false);
  }, [primaryActivity, setValue]);

  const currentPrimaryActivity = user.activities.find((activity) => activity.isPrimaryActivity)?.id;
  const primaryActivityOptions = [
    ...new Set<Exclude<ActivityId, typeof Activity.other> | undefined>([...availablePrimaryActivities, currentPrimaryActivity]),
  ].filter(filterBoolean);

  const secondaryActivityOptions = getSecondaryActivityOptions(
    user.activities.filter((activity) => !activity.isPrimaryActivity).map(({ id }) => id),
  );

  return (
    <div className={wrapperStyle}>
      <Controller
        control={control}
        name="primaryActivity"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.activities.primary.label')}</Field.Label>

            <Radio.Group className={primaryActivitiesStyle} onValueChange={(newValue) => onChange(newValue as ActivityId)} value={value}>
              {primaryActivityOptions.map((activity) => (
                <Radio.Root disabled={!includes(availablePrimaryActivities, activity)} key={activity} value={activity}>
                  <Radio.Label>{t(`common:activities.${activity}.label`)}</Radio.Label>
                  <Radio.Description>{t(`common:activities.${activity}.description`)}</Radio.Description>
                </Radio.Root>
              ))}
            </Radio.Group>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: true, message: t('validation:required') } }}
      />

      {primaryActivity === Activity.stylingAndInterior && <StylingAndInteriorWarning />}

      <FieldGroup.Root>
        <FieldGroup.Legend>{t('settings:fields.activities.secondary.label')}</FieldGroup.Legend>

        <Field.Root>
          <div className={secondaryActivitiesStyle}>
            {secondaryActivityOptions.map(
              (activity, index) =>
                activity !== primaryActivity && (
                  <Controller
                    control={control}
                    key={activity}
                    name={`secondaryActivities.${index}`}
                    render={({ field: { name, onChange, value } }) => (
                      <Checkbox.Root
                        checked={value}
                        disabled={!includes(availableActivities, activity)}
                        name={name}
                        onCheckedChange={onChange}
                      >
                        <Checkbox.Label>{t(`common:activities.${activity}.label`)}</Checkbox.Label>
                      </Checkbox.Root>
                    )}
                  />
                ),
            )}
          </div>
        </Field.Root>
      </FieldGroup.Root>

      {isOtherActivityChecked && (
        <Controller
          control={control}
          name="otherActivityInformation"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
            <Field.Root invalid={invalid} name={name}>
              <Field.Label>{t('settings:fields.activities.other.label')}</Field.Label>

              <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
          }}
        />
      )}
    </div>
  );
};
